import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { filterService, FilterOptions } from '@/services/filterService';

// Interface para o estado de filtros
export interface ActiveFilters {
  Demographics: {
    gender: string[];
    ageInterval: string[];
    ethnicity: string[];
  };
  Lifestyle: {
    statusClass: string[];
    lifestyle: string[];
    industry: string[];
    petFriendly: boolean | null;
  };
  Location: {
    continent: string[];
  };
  'NBA Preferences': {
    favoritePlayers: string[];
  };
}

// Estado inicial dos filtros
const initialFilters: ActiveFilters = {
  Demographics: {
    gender: [],
    ageInterval: [],
    ethnicity: [],
  },
  Lifestyle: {
    statusClass: [],
    lifestyle: [],
    industry: [],
    petFriendly: null,
  },
  Location: {
    continent: [],
  },
  'NBA Preferences': {
    favoritePlayers: [],
  },
};

// Interface para o contexto
interface FilterContextType {
  activeFilters: ActiveFilters;
  filterOptions: FilterOptions;
  isLoading: boolean;
  error: string | null;
  updateDemographicFilter: (category: keyof ActiveFilters['Demographics'], values: string[]) => void;
  updateLifestyleFilter: (category: keyof Omit<ActiveFilters['Lifestyle'], 'petFriendly'>, values: string[]) => void;
  updateLocationFilter: (category: keyof ActiveFilters['Location'], values: string[]) => void;
  setPetFriendly: (value: boolean | null) => void;
  updateNBAFilter: (category: keyof ActiveFilters['NBA Preferences'], values: string[]) => void;
  clearAllFilters: () => void;
  refreshFilters: () => Promise<void>;
  clearDemographicFilters: () => void;
  clearLifestyleFilters: () => void;
  clearNBAFilters: () => void;
  hasActiveFilters: () => boolean;
  getActiveFilterCount: () => number;
}

// Criação do contexto
const FilterContext = createContext<FilterContextType | undefined>(undefined);

// Provider do contexto
interface FilterProviderProps {
  children: ReactNode;
}

export function FilterProvider({ children }: FilterProviderProps) {
  const [activeFilters, setActiveFilters] = useState<ActiveFilters>(initialFilters);
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    Demographics: { gender: [], ageInterval: [], ethnicity: [] },
    Lifestyle: { lifestyle: [], industry: [], statusClass: [] },
    Location: { continent: [] },
    'NBA Preferences': { favoritePlayers: [] }
  });
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Função para atualizar filtros demográficos
  const updateDemographicFilter = (
    category: keyof ActiveFilters['Demographics'],
    values: string[]
  ) => {
    setActiveFilters(prev => ({
      ...prev,
      Demographics: {
        ...prev.Demographics,
        [category]: values,
      },
    }));
  };

  // Função para atualizar filtros de lifestyle
  const updateLifestyleFilter = (
    category: keyof Omit<ActiveFilters['Lifestyle'], 'petFriendly'>,
    values: string[]
  ) => {
    setActiveFilters(prev => ({
      ...prev,
      Lifestyle: {
        ...prev.Lifestyle,
        [category]: values,
      },
    }));
  };

  // Função para definir pet friendly
  const setPetFriendly = (value: boolean | null) => {
    setActiveFilters(prev => ({
      ...prev,
      Lifestyle: {
        ...prev.Lifestyle,
        petFriendly: value,
      },
    }));
  };

  // Função para atualizar filtros de localização
  const updateLocationFilter = (
    category: keyof ActiveFilters['Location'],
    values: string[]
  ) => {
    setActiveFilters(prev => ({
      ...prev,
      Location: {
        ...prev.Location,
        [category]: values,
      },
    }));
  };

  // Função para atualizar filtros da NBA
  const updateNBAFilter = (
    category: keyof ActiveFilters['NBA Preferences'],
    values: string[]
  ) => {
    setActiveFilters(prev => ({
      ...prev,
      'NBA Preferences': {
        ...prev['NBA Preferences'],
        [category]: values,
      },
    }));
  };

  // Carrega os filtros uma única vez
  useEffect(() => {
    const loadFilters = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const options = await filterService.getFilters();
        setFilterOptions(options);
      } catch (err) {
        setError('Failed to load filter options');
        console.error('Error loading filters:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadFilters();
  }, []);

  // Função para atualizar os filtros manualmente se necessário
  const refreshFilters = async (): Promise<void> => {
    try {
      const options = await filterService.getFilters();
      setFilterOptions(options);
    } catch (err) {
      console.error('Error refreshing filters:', err);
      throw err;
    }
  };

  // Função para limpar todos os filtros
  const clearAllFilters = () => {
    setActiveFilters(initialFilters);
  };

  // Função para limpar filtros demográficos
  const clearDemographicFilters = () => {
    setActiveFilters(prev => ({
      ...prev,
      Demographics: {
        gender: [],
        ageInterval: [],
        ethnicity: [],
      },
    }));
  };

  // Função para limpar filtros de lifestyle
  const clearLifestyleFilters = () => {
    setActiveFilters(prev => ({
      ...prev,
      Lifestyle: {
        ...prev.Lifestyle,
        statusClass: [],
        lifestyle: [],
        industry: [],
      },
    }));
  };

  // Função para limpar filtros da NBA
  const clearNBAFilters = () => {
    setActiveFilters(prev => ({
      ...prev,
      'NBA Preferences': {
        favoritePlayers: [],
      },
    }));
  };

  // Função para verificar se há filtros ativos
  const hasActiveFilters = (): boolean => {
    const { Demographics, Lifestyle, 'NBA Preferences': nbaPrefs } = activeFilters;
    
    // Check Demographics
    if (Object.values(Demographics).some(arr => arr.length > 0)) return true;
    
    // Check Lifestyle (excluding petFriendly)
    const { petFriendly, ...lifestyleFilters } = Lifestyle;
    if (Object.values(lifestyleFilters).some(arr => arr.length > 0)) return true;
    if (petFriendly !== null) return true;
    
    // Check NBA Preferences
    if (nbaPrefs.favoritePlayers.length > 0) return true;
    
    return false;
  };

  // Função para obter a contagem de filtros ativos
  const getActiveFilterCount = (): number => {
    const { Demographics, Lifestyle, 'NBA Preferences': nbaPrefs } = activeFilters;
    let count = 0;

    // Count demographic filters
    count += Object.values(Demographics).reduce(
      (sum, array) => sum + array.length,
      0
    );

    // Count lifestyle filters (excluding petFriendly)
    const { petFriendly, ...lifestyleFilters } = Lifestyle;
    count += Object.values(lifestyleFilters).reduce(
      (sum, array) => sum + (Array.isArray(array) ? array.length : 0),
      0
    );

    // Add 1 if petFriendly is set
    if (petFriendly !== null) {
      count += 1;
    }

    // Count NBA filters
    count += nbaPrefs.favoritePlayers.length;

    return count;
  };

  const value: FilterContextType = {
    activeFilters,
    filterOptions,
    isLoading,
    error,
    updateDemographicFilter,
    updateLifestyleFilter,
    updateLocationFilter,
    setPetFriendly,
    updateNBAFilter,
    clearAllFilters,
    refreshFilters,
    clearDemographicFilters,
    clearLifestyleFilters,
    clearNBAFilters,
    hasActiveFilters,
    getActiveFilterCount,
  };

  return (
    <FilterContext.Provider value={value}>
      {children}
    </FilterContext.Provider>
  );
}

// Hook para consumir o contexto
export const useFilters = (): FilterContextType => {
  const context = useContext(FilterContext);

  if (context === undefined) {
    throw new Error('useFilters deve ser usado dentro de um FilterProvider');
  }

  return context;
};
